import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import { adminAPI } from '../../utils/api';

const AdminSettingsPage = () => {
  const [form, setForm] = useState({ whatsapp_otp_enabled: false });
  const [loading, setLoading] = useState(false);
  const { success, error } = useToast();

  const handleSave = async () => {
    setLoading(true);
    try {
      await adminAPI.updateSettings(form);
      success('Settings saved!');
    } catch { error('Failed to save settings.'); }
    finally { setLoading(false); }
  };

  const SettingRow = ({ icon, title, desc, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid #F7F8FC' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ fontSize: '20px', marginTop: '2px' }}>{icon}</div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700 }}>{title}</div>
          <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>{desc}</div>
        </div>
      </div>
      {children}
    </div>
  );

  const Toggle = ({ value, onChange }) => (
    <div onClick={onChange} style={{ width: '44px', height: '24px', borderRadius: '12px', background: value ? '#FF6B00' : '#E2E8F0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: value ? '23px' : '3px', transition: 'left 0.2s' }}></div>
    </div>
  );

  return (
    <AdminShell
      title="Platform Settings"
      subtitle="Configure platform-wide settings"
      actions={
        <button onClick={handleSave} disabled={loading}
          style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 16px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* General */}
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '4px' }}>⚙️ General Settings</div>
          <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '16px' }}>Platform-wide configuration options</div>

          <SettingRow icon="📱" title="WhatsApp OTP" desc="Send OTP via WhatsApp instead of email. Requires WhatsApp Business API key.">
            <Toggle value={form.whatsapp_otp_enabled} onChange={() => setForm(p => ({ ...p, whatsapp_otp_enabled: !p.whatsapp_otp_enabled }))} />
          </SettingRow>

          <SettingRow icon="🔒" title="Admin Approval Required" desc="All new student registrations require admin approval before access.">
            <span style={{ fontSize: '11px', fontWeight: 800, background: '#E6F4EC', color: '#1B7F3E', borderRadius: '20px', padding: '3px 10px' }}>Always On</span>
          </SettingRow>

          <SettingRow icon="📧" title="Email Notifications" desc="Send approval and rejection emails to parents.">
            <span style={{ fontSize: '11px', fontWeight: 800, background: '#E6F4EC', color: '#1B7F3E', borderRadius: '20px', padding: '3px 10px' }}>Always On</span>
          </SettingRow>

          <SettingRow icon="🤖" title="Ask Guruji (AI)" desc="Allow students to ask doubts using AI. Uses Anthropic API credits.">
            <span style={{ fontSize: '11px', fontWeight: 800, background: '#E6F4EC', color: '#1B7F3E', borderRadius: '20px', padding: '3px 10px' }}>Always On</span>
          </SettingRow>
        </div>

        {/* Info */}
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '16px' }}>ℹ️ Platform Information</div>
          {[
            { label: 'Platform Name', value: 'MyPersonalGuruji' },
            { label: 'Domain', value: 'mypersonalguruji.com' },
            { label: 'CBSE Classes', value: 'Class 6 to Class 10' },
            { label: 'Learning Method', value: '1-4-7 Spaced Repetition' },
            { label: 'AI Model', value: 'Claude Sonnet (Anthropic)' },
            { label: 'Version', value: '1.0.0 — April 2026' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F7F8FC', fontSize: '13px' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>{item.label}</span>
              <span style={{ fontWeight: 700 }}>{item.value}</span>
            </div>
          ))}
        </div>

        {/* Legal */}
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '14px', padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#7C2D12', marginBottom: '8px' }}>⚠️ Legal & Compliance Reminder</div>
          <div style={{ fontSize: '12px', color: '#7C2D12', lineHeight: 1.6 }}>
            MyPersonalGuruji collects data of minors. Ensure your Privacy Policy, Terms of Service and DPDP Act 2023 compliance documents are live before onboarding students. Engage a lawyer specialising in EdTech data privacy.
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminSettingsPage;
